import { SceneMap, TabView } from "react-native-tab-view";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import VDBTabBar from "@components/atoms/TabView/components/VDBTabBar";
import {
  setAuxPayloadData,
  setCollection,
  setSelectedFilterTab,
  setSectionName,
  useSearchFiltersLayout,
  useSearchFiltersPayload,
  useSelectedFilterTab,
} from "@redux/slices/searchFilters";
import {
  RouteProp,
  useFocusEffect,
  useIsFocused,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import {
  filterSearchFilters,
  generateSections,
  generateSectionsComponents,
} from "@scenes/explore/utils";
import {
  FilterComponentsType,
  FilterScreenTypes,
  SectionTabType,
} from "@scenes/explore/types";
import { useDispatch, useSelector } from "react-redux";
import LayoutContainer from "@components/layout/LayoutContainer";
import { navigate } from "@navigations/utils/RootNavigation";
import {
  Animated,
  DeviceEventEmitter,
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  View,
} from "react-native";
import AdBanner from "@scenes/searchResults/AdsComponent";
import { BannerActivityType } from "@scenes/searchResults/types";
import { ProductCategory } from "@utils/constants";
import type { SceneRendererProps } from "react-native-tab-view/lib/typescript/src/types";
import Loader from "@components/atoms/Loader";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import styled from "styled-components/native";
import { useTranslation } from "react-i18next";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import Routes from "@navigations/Routes";
import { SpaceType } from "@components/atoms/Spacer/types";
import { VDBIcons } from "@utils/VDBIcons";
import { Container } from "@components/atoms";
import ButtonSection from "@components/atoms/Filters/ButtonSection";
import { useTheme } from "styled-components/native";
import { FilterScreenContext } from "./FilterScreenContext";
import {
  isLabGrownJewelrySearch,
  useLabGrownToast,
} from "./hooks/useLabGrownToast";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import { useAIChatBotConfig, useAppSettings } from "@redux/slices/globalSlice";
import { usePrivateAppGuard } from "@utils/usePrivateAppGuard";
import useProductArray from "@components/common/hooks/useProductArray";
import urlEventEmitter, { URL_EVENTS } from "@utils/custom-url-event-emitter";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { useJewelryConfiguratorState } from "@scenes/configurator/JewelryConfigurator/Context/JewelryConfiguratorContext";
import { getEffectiveEnterSecondFlow } from "@scenes/searchResults/utils/stripJewelryLinkageFromPayload";
import {
  setProductType,
  setSelectedProduct,
} from "@redux/slices/appStateSlice";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { getProductColor } from "@utils/product/productTheme";
import { ChatTooltip } from "@components/atoms/AISearchFloatingButton/ChatToolTip";
import { chatBotConfig } from "@navigations/utils/constants";
import FiltersTabSection from "@components/atoms/FiltersTabSection";
import { DesignOptions } from "@components/atoms/Filters/types";
import { useScrollbarWidth } from "@components/atoms/SectionContent/utils";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import { initializeSearchFilterHeight } from "../../iframe/height/searchFilterHeight";
import { useParentResizesIframe } from "../../hooks/useParentResizesIframe";

const FilterScreen = ({
  productType: productTypeProp,
  fromResultScreen: fromResultScreenProp,
  filterContainerWidth,
  skipFilterScreenForWeb,
  isOnSplitScreen,
  showScrollbars = true,
  enterSecondFlow = false,
  fromSplitScreen = false,
}: FilterScreenTypes) => {
  const { t } = useTranslation();
  const route = useRoute<RouteProp<any>>();
  const selectedTabRedux = useSelectedFilterTab();
  const userInfo = useUserInfo();
  const parentResizesIframe = useParentResizesIframe();
  useFocusEffect(
    useCallback(() => {
      if (Platform.OS !== "web" || !userInfo.isIFrame()) return;
      return initializeSearchFilterHeight();
    }, [userInfo])
  );
  // The tab pager locks the filters to the iframe viewport. In an embed the
  // parent page should scroll the full form instead.
  const sizeFiltersToContent =
    Platform.OS === "web" &&
    !!userInfo.isIFrame() &&
    !isOnSplitScreen &&
    parentResizesIframe;
  const productType = productTypeProp || route.params?.productType;
  const disableJewelrySettingStoneLinkage = useSelector(
    (state: any) => state.appState?.disableJewelrySettingStoneLinkage
  );
  const mEnterSecondFlow = getEffectiveEnterSecondFlow(
    route?.params?.enterSecondFlow ?? enterSecondFlow,
    productType,
    disableJewelrySettingStoneLinkage
  );
  const navigation = useNavigation();
  const aiChatBotConfig = useAIChatBotConfig();
  const maxContentLength = 1432;
  const fromResultScreen =
    typeof fromResultScreenProp === "boolean"
      ? fromResultScreenProp
      : typeof route?.params?.fromResultScreen === "boolean"
      ? route.params.fromResultScreen
      : typeof route?.params?.fromResultScreen === "string"
      ? route.params.fromResultScreen.toLowerCase() === "true"
      : false;
  const dispatch = useDispatch();
  const { isPDPLoaded, sectionName } = route.params ?? {};
  const { enableJewelryBuilder } = useOrgSettingsSlice();
  const rawFilters = useSearchFiltersLayout();

  const filters = useMemo(() => {
    if (!(mEnterSecondFlow && enableJewelryBuilder) || !rawFilters)
      return rawFilters;

    const removeCollectionDropdown = (node: any): any => {
      if (Array.isArray(node)) {
        return node
          .filter(
            (item) =>
              !(
                item &&
                typeof item === "object" &&
                item.type === DesignOptions.collection_dropdown
              )
          )
          .map(removeCollectionDropdown);
      }
      if (node && typeof node === "object") {
        return Object.keys(node).reduce((acc: any, key) => {
          acc[key] = removeCollectionDropdown(node[key]);
          return acc;
        }, {});
      }
      return node;
    };

    return removeCollectionDropdown(rawFilters);
  }, [rawFilters, mEnterSecondFlow, enableJewelryBuilder]);

  const filteredFilters =
    filters?.[productType]?.filter(
      (item) =>
        Array.isArray(item.price_modes) &&
        item.price_modes.includes(
          userInfo?.user?.price_mode ?? PriceModes.WHITE
        )
    ) ?? [];

  const sectionNameRedux = useSelector(
    (state: any) => state.searchFilters.sectionName
  );
  const currentCollections = useSelector(
    (state: any) => state.searchFilters.collection
  );
  const currentCollectionsRef = useRef(currentCollections);
  currentCollectionsRef.current = currentCollections;
  const payload = useSearchFiltersPayload(productType);
  const { hideSectionOnSplitScreen } = useProductArray();
  const theme = useTheme();
  const [index, setIndex] = useState(0);
  const tabBarPositionRef = React.useRef(new Animated.Value(0));
  const [routes, setRoutes] = useState<SectionTabType[]>([]);
  const [scenes, setScenes] = useState<FilterComponentsType>();
  const [fromJewelryResultScreen, setFromJewelryResultScreen] = useState(false);
  const [showAdBanner, setShowAdBanner] = useState(false);
  const [showLabgrownPopup, setShowLabgrownPopUp] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [token, setToken] = useState<any | null>();
  const isMountedRef = useRef(true);
  const getAppSettings = useAppSettings();
  const isFocused = useIsFocused();
  const { triggerLabGrownToast } = useLabGrownToast({
    productType,
    activeTabKey: routes?.length > 0 ? routes[index]?.key : undefined,
    getAppSettings,
    userInfo,
    t,
  });
  const hasSyncedIncomingSearchRef = useRef(false);
  const lastIncomingSearchRef = useRef(
    route?.params?.saved_search || route?.params?.new_search
  );
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(true);
  const { shouldBlock: shouldBlockPublicAccess } =
    usePrivateAppGuard("go-back");
  let categoryFilters = filterSearchFilters(
    filters?.[productType],
    userInfo.user.price_mode
  );

  const filterTabs = useMemo(() => {
    return categoryFilters?.find((item: any) => item.name === sectionName)
      ?.filters_tab_section;
  }, [categoryFilters, sectionName]);

  const { itemDetail: mItem, stoneItemDetail: mStoneItemDetail } =
    useJewelryConfiguratorState();

  const normalizedValue =
    fromResultScreen === "true"
      ? true
      : fromResultScreen === "false"
      ? false
      : fromResultScreen;
  const [key, setKey] = useState(0);

  const showHeaderButtons = useMemo(() => {
    return (
      theme.isLargeScreen &&
      !normalizedValue &&
      ![ProductCategory.JEWELRY, ProductCategory.LABGROWN_JEWELRY].includes(
        productType
      )
    );
  }, [theme.isLargeScreen, normalizedValue, productType]);

  const showLegacyFilterScreenButton = useMemo(
    () =>
      theme.isLargeScreen
        ? normalizedValue
        : [ProductCategory.JEWELRY, ProductCategory.LABGROWN_JEWELRY].includes(
            productType
          )
        ? true
        : ![ProductCategory.JEWELRY, ProductCategory.LABGROWN_JEWELRY].includes(
            productType
          ),
    [theme.isLargeScreen, normalizedValue, productType]
  );

  const showFilterScreenButton = useMemo(
    () =>
      theme.isLargeScreen
        ? true
        : [ProductCategory.JEWELRY, ProductCategory.LABGROWN_JEWELRY].includes(
            productType
          )
        ? true
        : ![ProductCategory.JEWELRY, ProductCategory.LABGROWN_JEWELRY].includes(
            productType
          ),
    [theme.isLargeScreen, normalizedValue, productType]
  );

  const showDiamondClassicAiTabs = useMemo(() => {
    const isPriceModeEnabled =
      chatBotConfig.floatingButtonVisibility.enabledPriceModes.includes(
        userInfo?.getPriceMode()
      );
    const includedProductTypes = [
      ProductCategory.DIAMOND,
      ProductCategory.LAB_GROWN_DIAMOND,
      ProductCategory.JEWELRY,
    ];
    return (
      userInfo?.user?.feature_settings?.enable_chatbot &&
      isPriceModeEnabled &&
      !theme.isMobile &&
      includedProductTypes.includes(productType as ProductCategory) &&
      aiChatBotConfig?.show_ai_chat_on_filter_split_screen
    );
  }, [theme.isMobile, productType, userInfo]);
  const scrollableTabBar = showDiamondClassicAiTabs;
  const selectedTab =
    showDiamondClassicAiTabs && filterTabs
      ? selectedTabRedux
      : "classic_search";

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isFocused) return;
    dispatch(setSelectedProduct(getProductColor(productType)));
    dispatch(setProductType(productType));
  }, [productType, isFocused]);

  useEffect(() => {
    if (!isFocused) return;
    handleChangeIndex?.(sectionName || sectionNameRedux);
    dispatch(setAuxPayloadData(payload));
    if (!sectionNameRedux) {
      dispatch(setSectionName(sectionName || filteredFilters?.[0]?.name));
    }
  }, []);

  const handleChangeIndex = (name: string) => {
    if (name) {
      const activeIndex = filteredFilters?.findIndex(
        (filter: any) => filter.name === name
      );

      setIndex(activeIndex >= 0 ? activeIndex : 0);
    }
  };

  useEffect(() => {
    if (route?.params?.open_ai_search_widget) {
      DeviceEventEmitter.emit("OPEN_AI_SEARCH_WIDGET");
    }
  }, [route?.params?.open_ai_search_widget]);
  useEffect(() => {
    if (!isFocused || !sectionName) return;
    dispatch(setSectionName(sectionName));
    handleChangeIndex?.(sectionName);
  }, [sectionName, isFocused]);

  useEffect(() => {
    if (!sectionNameRedux) return;
    handleChangeIndex(sectionNameRedux);
  }, [sectionNameRedux, filteredFilters]);

  useEffect(() => {
    setKey((prev) => prev + 1);
  }, [productType]);

  useEffect(() => {
    tabBarPositionRef.current.setValue(index);
  }, [index]);

  useEffect(() => {
    const jewelryStyle = mEnterSecondFlow
      ? mStoneItemDetail?.jewelry_style
      : mItem?.jewelry_style;

    const isEarring = jewelryStyle?.includes("Earring");
    const isRing =
      (jewelryStyle?.includes("ring") || jewelryStyle?.includes("Ring")) &&
      !isEarring;
    const isPendant =
      jewelryStyle?.includes("pendant") || jewelryStyle?.includes("Pendant");

    if (enableJewelryBuilder && (isRing || isPendant) && mEnterSecondFlow) {
      categoryFilters = categoryFilters.filter(
        (filter) => filter.name !== "Pairs"
      );
    }

    const sections = generateSections(categoryFilters, t);
    const components = generateSectionsComponents(
      categoryFilters,
      theme,
      productType,
      normalizedValue,
      filterContainerWidth,
      setContainerWidth,
      isOnSplitScreen ?? false,
      showScrollbars,
      mEnterSecondFlow,
      undefined,
      undefined,
      sizeFiltersToContent
    );

    setRoutes(sections);
    setScenes(components);
  }, [
    filters,
    productType,
    userInfo.user.price_mode,
    index,
    payload,
    userInfo.user.locale,
    mEnterSecondFlow,
    sizeFiltersToContent,
  ]);

  useEffect(() => {
    // Background builder screens stay mounted. Syncing their payload would
    // overwrite the setting filters the user just changed.
    if (!isFocused) return;
    if (payload && Platform.OS === "web") {
      dispatch(setAuxPayloadData(payload));
    }
  }, [payload, isFocused]);

  useEffect(() => {
    if (fromResultScreen) return;
    const subscription = DeviceEventEmitter.addListener(
      "AI_FILTERS_APPLIED",
      () => {
        if (!isMountedRef.current) return;
        setKey((prev) => prev + 1);
        dispatch(setAuxPayloadData(payload));
      }
    );

    return () => {
      subscription.remove();
    };
  }, []);

  useEffect(() => {
    let collectionParam = route?.params?.collection;
    let collectionSource: "route" | "url" | "none" = collectionParam
      ? "route"
      : "none";

    if (Platform.OS === "web" && !collectionParam) {
      const searchParams = new URLSearchParams(window.location.search);
      collectionParam = searchParams.get("collection");
      if (collectionParam) collectionSource = "url";
    }

    if (collectionParam) {
      let collectionsToSet: string[] = [];

      if (typeof collectionParam === "string") {
        collectionsToSet = collectionParam
          .split(",")
          .map((c) => c.trim())
          .filter((c) => c);
      } else if (Array.isArray(collectionParam)) {
        collectionsToSet = collectionParam;
      }

      if (collectionsToSet.length > 0) {
        // URL only has the parent (e.g. Rings). Apply re-navigates and must
        // not replace [Rings, Engagement Rings] with [Rings].
        // Read collections from a ref so a later dropdown change (All-Jewelry
        // → Rings) does not re-run this effect and snap the selection back.
        const reduxCollection = currentCollectionsRef.current ?? [];
        const alreadyHydrated = collectionsToSet.every((c) =>
          reduxCollection.includes(c)
        );
        // Apply navigates without a collection param, so this effect falls
        // through to window.location.search. That query still has the explore
        // selection (All-Collections) after the dropdown moves to Essentials.
        const staleUrlCollection =
          collectionSource === "url" &&
          reduxCollection.length > 0 &&
          !alreadyHydrated;
        if (alreadyHydrated || staleUrlCollection) {
          return;
        }
        dispatch(setCollection(collectionsToSet));
      }
    }
  }, [route?.params?.collection, dispatch]);

  useEffect(() => {
    let isMounted = true;
    const fetchToken = async () => {
      try {
        const storedToken = await userInfo.getToken();
        // Avoid setting state after unmount.
        if (isMounted) setToken(storedToken);
      } catch (error) {}
    };

    fetchToken();
    return () => {
      isMounted = false;
    };
  }, []);

  useFocusEffect(
    useCallback(() => {
      if (token) {
        setShowAdBanner(true);
      }
      return () => {
        setShowAdBanner(false);
        Keyboard.dismiss();
      };
    }, [token, productType])
  );

  useEffect(() => {
    const filterSectionSub = DeviceEventEmitter.addListener(
      "filterSectionNameChange",
      ({ sectionName: name }) => {
        if (!isMountedRef.current) return;
        if (normalizedValue) handleChangeIndex?.(name);
      }
    );

    const jewelryTypeSub = urlEventEmitter.on(
      URL_EVENTS.FOCUS_JEWELRY_TYPE,
      () => {}
    );

    return () => {
      filterSectionSub.remove();
      jewelryTypeSub();
    };
  }, [isFocused, sectionName, normalizedValue]);

  useEffect(() => {
    if (
      [ProductCategory.JEWELRY, ProductCategory.LABGROWN_JEWELRY].includes(
        productType
      ) &&
      normalizedValue
    )
      setFromJewelryResultScreen(true);
  }, [normalizedValue]);

  useEffect(() => {
    const incomingSearch =
      route?.params?.saved_search || route?.params?.new_search;
    if (lastIncomingSearchRef.current !== incomingSearch) {
      lastIncomingSearchRef.current = incomingSearch;
      hasSyncedIncomingSearchRef.current = false;
    }

    if (
      !showLabgrownPopup &&
      (!normalizedValue || theme.isLargeScreen) &&
      isFocused
    ) {
      const fromSavedSearch = isLabGrownJewelrySearch(incomingSearch);
      const tabIsLabGrown =
        routes[index]?.key === SearchFilterTypeEnum.LAB_GROWN_JEWELRY;

      // Deeplink params merge onto the previous screen, so the Lab-Grown tab
      // can still be selected while the incoming search is regular jewelry.
      // Wait until the tab catches up, then use the tab for later switches.
      if (
        fromSavedSearch !== undefined &&
        !hasSyncedIncomingSearchRef.current &&
        fromSavedSearch !== tabIsLabGrown
      ) {
        return;
      }
      hasSyncedIncomingSearchRef.current = true;

      const shouldShowPopup = triggerLabGrownToast();
      if (shouldShowPopup) setShowLabgrownPopUp(true);
    }
  }, [
    showLabgrownPopup,
    normalizedValue,
    index,
    triggerLabGrownToast,
    isFocused,
    routes,
    route?.params?.saved_search,
    route?.params?.new_search,
  ]);

  // Avoid recreating `SceneMap` on every TabView render.
  const sceneMap = React.useMemo(() => SceneMap(scenes ?? {}), [scenes]);

  const renderScene = useCallback((props: any) => sceneMap(props), [sceneMap]);

  const renderLegacyTabBar = useCallback(
    (tabBarProps: SceneRendererProps) => {
      if (scrollableTabBar) {
        return null;
      }

      if (!tabBarProps?.navigationState?.routes) {
        return null;
      }

      if (fromJewelryResultScreen && theme.isLargeScreen) {
        return null;
      }

      if (
        theme.isMobile &&
        routes?.length === 1 &&
        filters?.[productType]?.[0]?.isCollection
      ) {
        return null;
      }

      return theme.isLargeScreen ? (
        <View
          style={{
            backgroundColor: theme["core-01"],
            borderBottomWidth:
              !fromResultScreen ||
              (fromResultScreen && !hideSectionOnSplitScreen(productType))
                ? 1
                : 0,
            borderColor: theme["core-03"],
          }}
        >
          <View
            style={{
              alignContent: "center",
              alignSelf: "center",
              width: theme.isLargeScreen && !isOnSplitScreen ? "80%" : "100%",
            }}
          >
            <Container
              maxWidth={theme.isLargeScreen ? maxContentLength : undefined}
            >
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                }}
              >
                {(!fromResultScreen ||
                  (fromResultScreen &&
                    !hideSectionOnSplitScreen(productType))) && (
                  <VDBTabBar tabBarProps={tabBarProps} props={{}} />
                )}

                <View style={{ position: "relative" }}>
                  {showHeaderButtons && (
                    <ButtonSection
                      isPDPLoaded={isPDPLoaded}
                      filterContainerWidth={400}
                      fromResultScreen={normalizedValue}
                      skipFilterScreenForWeb={skipFilterScreenForWeb}
                      filters={filters?.[productType]}
                      enterSecondFlow={mEnterSecondFlow}
                      isOnSplitScreen={isOnSplitScreen}
                    />
                  )}
                </View>
              </View>
            </Container>
          </View>
        </View>
      ) : (
        <VDBTabBar tabBarProps={tabBarProps} props={{}} />
      );
    },
    [
      scrollableTabBar,
      showHeaderButtons,
      fromJewelryResultScreen,
      theme,
      fromResultScreen,
      isOnSplitScreen,
      userInfo.user.locale,
      filters,
      productType,
      isPDPLoaded,
      normalizedValue,
      skipFilterScreenForWeb,
      mEnterSecondFlow,
      hideSectionOnSplitScreen,
    ]
  );

  const renderEnhancedTabBar = useCallback(
    (tabBarProps: SceneRendererProps) => {
      if (scrollableTabBar) {
        return null;
      }

      const sections = generateSections(categoryFilters, t);
      const isSingleSectionWithEmptyTitle =
        sections.length === 1 &&
        (!sections[0]?.title || sections[0]?.title.trim() === "");
      if (
        !tabBarProps?.navigationState?.routes ||
        isSingleSectionWithEmptyTitle
      ) {
        return null;
      }

      if (fromJewelryResultScreen && theme.isLargeScreen) {
        return null;
      }

      if (
        theme.isMobile &&
        routes?.length === 1 &&
        filters?.[productType]?.[0]?.isCollection
      ) {
        return null;
      }
      const isSplitScreenVisible =
        fromResultScreen && !hideSectionOnSplitScreen(productType);

      return theme.isLargeScreen ? (
        <View
          style={{
            alignItems: isSplitScreenVisible ? undefined : "center",
            paddingTop: !fromResultScreen ? SpaceType.Double : 0,
            ...(isSplitScreenVisible && {
              borderBottomWidth: 1,
              borderBottomColor: theme["core-03"],
            }),
          }}
        >
          <View
            style={{
              width: isSplitScreenVisible ? "100%" : "80%",
              marginRight: useScrollbarWidth(),
            }}
          >
            <Container
              noPadding={isSplitScreenVisible}
              maxWidth={
                theme.isLargeScreen || theme.isTablet
                  ? maxContentLength
                  : undefined
              }
            >
              <View
                style={{
                  paddingHorizontal: isSplitScreenVisible
                    ? 0
                    : SpaceType.Double,
                  backgroundColor: theme["core-01"],
                  borderRadius: SpaceType.Basic,
                }}
              >
                {(!fromResultScreen ||
                  (fromResultScreen &&
                    !hideSectionOnSplitScreen(productType))) && (
                  <VDBTabBar tabBarProps={tabBarProps} props={{}} />
                )}
              </View>
            </Container>
          </View>
        </View>
      ) : theme.isTablet && !fromSplitScreen ? (
        <View style={{ width: "100%" }}>
          <View
            style={{
              borderRadius: SpaceType.Basic,
              marginHorizontal: SpaceType.Double,
              backgroundColor: theme["core-01"],
              marginTop: SpaceType.Double,
            }}
          >
            <View
              style={{
                marginHorizontal: SpaceType.Double,
              }}
            >
              <VDBTabBar tabBarProps={tabBarProps} props={{}} />
            </View>
          </View>
        </View>
      ) : (
        <VDBTabBar tabBarProps={tabBarProps} props={{}} />
      );
    },
    [
      scrollableTabBar,
      showHeaderButtons,
      fromJewelryResultScreen,
      theme,
      fromResultScreen,
      userInfo.user.locale,
      categoryFilters,
      t,
      fromSplitScreen,
      filters,
      productType,
      routes,
      hideSectionOnSplitScreen,
    ]
  );

  const renderScrollableTabBar = useCallback(() => {
    if (fromJewelryResultScreen && theme.isLargeScreen) {
      return null;
    }

    if (
      theme.isMobile &&
      routes?.length === 1 &&
      filters?.[productType]?.[0]?.isCollection
    ) {
      return null;
    }

    const tabBarProps = {
      navigationState: { index, routes },
      position: tabBarPositionRef.current,
      jumpTo: (mKey: string) => {
        const targetIndex = routes.findIndex((r) => r.key === mKey);
        if (targetIndex === -1) return;
        if (fromSplitScreen && !isOnSplitScreen) return;

        const newSectionName = filteredFilters?.[targetIndex]?.name;
        setIndex(targetIndex);
        dispatch(setSectionName(newSectionName));
        navigation.setParams({
          ...route.params,
          sectionName: newSectionName,
        });
      },
    };

    return theme.isLargeScreen ? (
      <View
        style={{
          backgroundColor: theme["core-01"],
          borderBottomWidth:
            !fromResultScreen ||
            (fromResultScreen && !hideSectionOnSplitScreen(productType))
              ? 1
              : 0,
          borderColor: theme["core-03"],
          marginTop: SpaceType.Double,
          borderRadius: SpaceType.Basic,
        }}
      >
        <Container
          maxWidth={theme.isLargeScreen ? maxContentLength : undefined}
        >
          {(!fromResultScreen ||
            (fromResultScreen && !hideSectionOnSplitScreen(productType))) && (
            <VDBTabBar tabBarProps={tabBarProps} props={{}} />
          )}
        </Container>
      </View>
    ) : theme.isTablet && !fromSplitScreen ? (
      <View style={{ width: "100%" }}>
        <View
          style={{
            borderRadius: SpaceType.Basic,
            backgroundColor: theme["core-01"],
            marginTop: SpaceType.Double,
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <View
            style={{
              marginHorizontal: SpaceType.Double,
              flex: 1,
            }}
          >
            <VDBTabBar tabBarProps={tabBarProps} props={{}} />
          </View>
          <Pressable
            style={{
              paddingRight: SpaceType.Double,
              padding: SpaceType.Double,
            }}
            onPress={() => {
              navigate(Routes.GlobalNavigator, {
                screen: Routes.SearchText,
                initial: true,
                params: { productType },
              });
            }}
          >
            <VDBIcon
              color={theme["core-05"]}
              size={24}
              variant={IconsVariant.VDBCustom}
              name={VDBIcons.MAIN_SEARCH}
            />
          </Pressable>
        </View>
      </View>
    ) : (
      <VDBTabBar tabBarProps={tabBarProps} props={{}} />
    );
  }, [
    index,
    routes,
    fromJewelryResultScreen,
    theme,
    fromResultScreen,
    isOnSplitScreen,
    filters,
    productType,
    hideSectionOnSplitScreen,
    fromSplitScreen,
    filteredFilters,
    dispatch,
    navigation,
    route.params,
  ]);

  const renderScrollableFiltersTabSection = useCallback(() => {
    if (!showDiamondClassicAiTabs || !filterTabs) {
      return null;
    }
    return (
      <FiltersTabSection
        filtersLayout={filterTabs}
        selectedTab={selectedTab}
        setSelectedTab={(tab: string) => dispatch(setSelectedFilterTab(tab))}
        fromSplitScreen={fromSplitScreen}
        productType={productType}
        sectionName={
          sectionName || sectionNameRedux || filteredFilters?.[0]?.name
        }
      />
    );
  }, [
    showDiamondClassicAiTabs,
    filterTabs,
    selectedTab,
    dispatch,
    fromSplitScreen,
    productType,
    sectionName,
    sectionNameRedux,
    filteredFilters,
  ]);

  if (!routes?.length || !scenes) return <Loader />;

  const searchIconPressed = () => {
    navigate(Routes.GlobalNavigator, {
      screen: Routes.SearchText,
      initial: true,
      params: {
        productType,
      },
    });
  };

  if (shouldBlockPublicAccess) {
    return <View />;
  }

  const renderFiltersTabSection = () => {
    if (!showDiamondClassicAiTabs || !filterTabs) {
      return null;
    }
    return (
      <FiltersTabSection
        filtersLayout={filterTabs}
        selectedTab={selectedTab}
        setSelectedTab={(tab: string) => dispatch(setSelectedFilterTab(tab))}
        fromSplitScreen={fromSplitScreen}
        productType={productType}
        sectionName={
          sectionName || sectionNameRedux || filteredFilters?.[0]?.name
        }
      />
    );
  };

  const renderChatTooltip = () => {
    return (
      <ChatTooltip
        isOpen={true}
        onClose={() => {}}
        theme={theme}
        fromFilterScreen={true}
        fromSplitScreen={isOnSplitScreen}
        showWelcomeScreen={showWelcomeScreen}
        setShowWelcomeScreen={(flag: boolean) => {
          setShowWelcomeScreen(flag);
        }}
      />
    );
  };

  const onTabIndexChange = (value: number) => {
    if (!isMountedRef.current) return;
    if (fromSplitScreen && !isOnSplitScreen) {
      return;
    }
    const newSectionName = filteredFilters?.[value]?.name;
    setIndex(value);

    dispatch(setSectionName(newSectionName));

    navigation.setParams({
      ...route.params,
      sectionName: newSectionName,
    });
  };

  const embeddedTabBarProps = {
    navigationState: { index, routes },
    layout: {
      width: Platform.OS === "web" ? window.innerWidth : 0,
      height: 0,
    },
    position: tabBarPositionRef.current,
    jumpTo: (sectionKey: string) => {
      const targetIndex = routes.findIndex((item) => item.key === sectionKey);
      if (targetIndex === -1) return;
      const newSectionName = filteredFilters?.[targetIndex]?.name;
      setIndex(targetIndex);
      dispatch(setSectionName(newSectionName));
      navigation.setParams({
        ...route.params,
        sectionName: newSectionName,
      });
    },
  };

  const renderEmbeddedFilters = (
    renderTabBar: (tabBarProps: any) => React.ReactNode
  ) => {
    const ActiveScene = scenes?.[routes[index]?.key];
    return (
      <>
        {renderTabBar(embeddedTabBarProps)}
        {ActiveScene ? <ActiveScene /> : null}
      </>
    );
  };

  const renderLegacyFilterContent = () => (
    <>
      <KeyboardAvoidingView
        style={sizeFiltersToContent ? { flexGrow: 0 } : { flex: 1 }}
        behavior={
          sizeFiltersToContent
            ? undefined
            : Platform.OS === "ios"
            ? "padding"
            : "height"
        }
        keyboardVerticalOffset={
          Platform.OS === "ios" ? 64 : Platform.OS === "android" ? 100 : 0
        }
      >
        {sizeFiltersToContent ? (
          renderEmbeddedFilters(renderLegacyTabBar)
        ) : (
          <TabView
            navigationState={{
              index,
              routes,
            }}
            lazy={!theme.isLargeScreen}
            renderScene={renderScene}
            onIndexChange={onTabIndexChange}
            renderTabBar={renderLegacyTabBar}
            animationEnabled={false}
            swipeEnabled={
              theme.isLargeScreen
                ? false
                : !fromJewelryResultScreen ||
                  !hideSectionOnSplitScreen(productType)
            }
          />
        )}
        {!(
          [ProductCategory.JEWELRY, ProductCategory.LABGROWN_JEWELRY].includes(
            productType
          ) && normalizedValue
        ) &&
          !theme.isLargeScreen && (
            <LegacySearchIconContainer onPress={() => searchIconPressed()}>
              <VDBIcon
                color={theme["core-05"]}
                size={24}
                variant={IconsVariant.VDBCustom}
                name={VDBIcons.MAIN_SEARCH}
              />
            </LegacySearchIconContainer>
          )}

        {showAdBanner && (
          <AdBanner
            productType={productType}
            activityType={BannerActivityType.ProductSwitch}
            closeBanner={() => {
              setShowAdBanner(false);
            }}
          />
        )}
      </KeyboardAvoidingView>
      {showLegacyFilterScreenButton && (
        <ButtonSection
          isPDPLoaded={isPDPLoaded}
          filterContainerWidth={343}
          fromResultScreen={normalizedValue}
          skipFilterScreenForWeb={skipFilterScreenForWeb}
          containerBackgroundColor={"core-01"}
          filters={filters?.[productType]}
          enterSecondFlow={mEnterSecondFlow}
          isOnSplitScreen={isOnSplitScreen}
        />
      )}
    </>
  );

  const renderEnhancedFilterContent = () => (
    <>
      <KeyboardAvoidingView
        style={sizeFiltersToContent ? { flexGrow: 0 } : { flex: 1 }}
        behavior={
          sizeFiltersToContent
            ? undefined
            : Platform.OS === "ios"
            ? "padding"
            : "height"
        }
        keyboardVerticalOffset={
          Platform.OS === "ios" ? 64 : Platform.OS === "android" ? 100 : 0
        }
      >
        {sizeFiltersToContent ? (
          renderEmbeddedFilters(renderEnhancedTabBar)
        ) : (
          <TabView
            navigationState={{
              index,
              routes,
            }}
            lazy={!theme.isLargeScreen}
            renderScene={renderScene}
            onIndexChange={onTabIndexChange}
            renderTabBar={renderEnhancedTabBar}
            animationEnabled={false}
            swipeEnabled={
              theme.isLargeScreen
                ? false
                : !fromJewelryResultScreen ||
                  !hideSectionOnSplitScreen(productType)
            }
          />
        )}

        {showAdBanner && (
          <AdBanner
            productType={productType}
            activityType={BannerActivityType.ProductSwitch}
            closeBanner={() => {
              setShowAdBanner(false);
            }}
          />
        )}
      </KeyboardAvoidingView>
      {((showFilterScreenButton && !userInfo?.isIFrame()) ||
        (userInfo?.isIFrame() && fromResultScreen)) && (
        <ButtonSection
          isPDPLoaded={isPDPLoaded}
          filterContainerWidth={343}
          fromResultScreen={normalizedValue}
          skipFilterScreenForWeb={skipFilterScreenForWeb}
          containerBackgroundColor={"core-01"}
          filters={filters?.[productType]}
          enterSecondFlow={mEnterSecondFlow}
          isOnSplitScreen={isOnSplitScreen}
          showDiamondClassicAiTabs={showDiamondClassicAiTabs}
        />
      )}
    </>
  );

  const filterScreenContextValue = {
    containerWidth,
    setContainerWidth,
    fromResultScreen: normalizedValue,
    productType,
    scrollableTabBar,
    renderScrollableTabBar: scrollableTabBar
      ? renderScrollableTabBar
      : undefined,
    renderScrollableFiltersTabSection: scrollableTabBar
      ? renderScrollableFiltersTabSection
      : undefined,
  };

  if (!showDiamondClassicAiTabs) {
    return (
      <FilterScreenContext.Provider key={key} value={filterScreenContextValue}>
        <LayoutContainer
          nativeID={sizeFiltersToContent ? "iframe-filter-root" : undefined}
          contentSized={sizeFiltersToContent}
          backgroundColor={"core-02"}
        >
          {renderLegacyFilterContent()}
        </LayoutContainer>
      </FilterScreenContext.Provider>
    );
  }

  return (
    <LayoutContainer
      nativeID={sizeFiltersToContent ? "iframe-filter-root" : undefined}
      contentSized={sizeFiltersToContent}
      backgroundColor={"core-02"}
    >
      <FilterScreenContext.Provider key={key} value={filterScreenContextValue}>
        {!scrollableTabBar && renderFiltersTabSection()}
        {userInfo?.isIFrame() && !fromResultScreen && (
          <ButtonSection
            isPDPLoaded={isPDPLoaded}
            filterContainerWidth={343}
            fromResultScreen={normalizedValue}
            skipFilterScreenForWeb={skipFilterScreenForWeb}
            containerBackgroundColor={"core-01"}
            filters={filters?.[productType]}
            enterSecondFlow={mEnterSecondFlow}
            isOnSplitScreen={isOnSplitScreen}
            showDiamondClassicAiTabs={showDiamondClassicAiTabs}
          />
        )}
        {selectedTab === "vdb_ai_search" ? (
          (theme.isTablet && fromResultScreen) || theme.isLargeScreen ? (
            <>
              {scrollableTabBar && (
                <View style={{ width: "100%" }}>
                  {renderScrollableFiltersTabSection()}
                </View>
              )}

              {renderChatTooltip()}
            </>
          ) : (
            <KeyboardAwareScrollView
              bottomOffset={20}
              keyboardShouldPersistTaps="always"
              style={{ flex: 1 }}
              enabled={theme.isTablet && showWelcomeScreen}
              scrollEnabled={theme.isTablet && showWelcomeScreen}
              contentContainerStyle={{
                justifyContent: "center",
                alignItems: "center",
                flex: 1,
              }}
            >
              {scrollableTabBar && (
                <View style={{ width: "100%" }}>
                  {renderScrollableFiltersTabSection()}
                </View>
              )}

              {renderChatTooltip()}
            </KeyboardAwareScrollView>
          )
        ) : (
          renderEnhancedFilterContent()
        )}
      </FilterScreenContext.Provider>
    </LayoutContainer>
  );
};

const LegacySearchIconContainer = styled.TouchableOpacity`
  padding: ${SpaceType.Double}px;
  position: absolute;
  right: 0px;
  justify-content: center;
  align-items: center;
`;

export default React.memo(FilterScreen, (prevProps, nextProps) => {
  return (
    prevProps.productType === nextProps.productType &&
    prevProps.fromResultScreen === nextProps.fromResultScreen &&
    prevProps.filterContainerWidth === nextProps.filterContainerWidth &&
    prevProps.skipFilterScreenForWeb === nextProps.skipFilterScreenForWeb &&
    prevProps.isOnSplitScreen === nextProps.isOnSplitScreen &&
    prevProps.fromSplitScreen === nextProps.fromSplitScreen &&
    prevProps.scrollableTabBar === nextProps.scrollableTabBar
  );
});
